import { memo } from 'react';
import { getTooltipStyle, LABEL_STYLE } from '../shared/constants';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList,
} from 'recharts';
import { ChartWrapper } from '../shared/ChartWrapper';
import { NoChartData } from '../shared/NoChartData';
import { useResponsiveYAxisWidth } from '../shared/useResponsiveYAxisWidth';
import { useInnovationData } from './useInnovationData';

const DELIVERY_COLORS = {
  Innovation: '#8b5cf6',
  Scaling: '#22c55e',
  Demand: '#f59e0b',
  Other: '#6b7280',
};

export const InnovationPartnerChart = memo(function InnovationPartnerChart() {
  const { partnerData, totalCount, innovationResults } = useInnovationData();
  const yAxisWidth = useResponsiveYAxisWidth(70);
  if (!partnerData || partnerData.totalPartnerEntries === 0) return (
    <ChartWrapper title="Innovation Partners">
      <NoChartData message="No partner data on innovation results" detail="This chart shows external partners contributing to innovation results, categorized by delivery type (Innovation, Scaling, Demand). None of the innovation results in the current selection have partner entries." />
    </ChartWrapper>
  );

  const { topPartners, byDeliveryType, totalPartnerEntries } = partnerData;

  const deliveryData = Object.entries(byDeliveryType)
    .filter(([, count]) => count > 0)
    .map(([type, count]) => ({ name: type, value: count }));

  const partnerBarData = topPartners.map(p => ({
    name: p.acronym,
    fullName: p.fullName,
    count: p.resultCount,
    types: (p.deliveryTypes || []).join(', ') || 'Not specified',
  }));

  const exportData = topPartners.map(p => ({
    Partner: p.acronym,
    'Full Name': p.fullName,
    'Result Count': p.resultCount,
    'Delivery Types': (p.deliveryTypes || []).join(', '),
  }));

  return (
    <ChartWrapper title="External Partners on Innovation Results" info="External partners involved in innovation results, organized by their delivery role (Demand, Innovation, Scaling)." chartData={exportData} exportFilename="innovation-partners" sourceResults={innovationResults}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Partner Delivery Type Roles</h5>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie data={deliveryData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={45} paddingAngle={2}
                animationBegin={0} animationDuration={400} label={({ name, value }) => `${name} (${value})`}>
                {deliveryData.map((entry) => (
                  <Cell key={entry.name} fill={DELIVERY_COLORS[entry.name] || '#6b7280'} />
                ))}
              </Pie>
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: DELIVERY_COLORS[d.name] || '#1e293b' }}>{d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.value} partner{d.value !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
            </PieChart>
          </ResponsiveContainer>
          <p className="text-xs text-gray-400 text-center mt-1">{totalPartnerEntries} unique partners across {totalCount} innovation results</p>
        </div>
        <div>
          <h5 className="text-sm font-medium text-gray-700 mb-2">Top Partners by Innovation Results</h5>
          <ResponsiveContainer width="100%" height={Math.max(250, partnerBarData.length * 22)}>
            <BarChart data={partnerBarData} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={yAxisWidth} />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div style={{ background: '#fff', padding: '8px 12px', borderRadius: '8px', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', fontSize: '13px', lineHeight: 1.4 }}>
                    <div style={{ fontWeight: 600, color: '#1e293b' }}>{d.fullName || d.name}</div>
                    <div style={{ color: '#4b5563' }}>{d.count} result{d.count !== 1 ? 's' : ''}</div>
                  </div>
                );
              }} />
              <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} animationBegin={0} animationDuration={400}>
                <LabelList dataKey="count" position="right" style={LABEL_STYLE} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </ChartWrapper>
  );
});
